import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"
import { Badge, type BadgeProps } from "@coval/ui"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const SEVERITY_THRESHOLD = 0.5
const TIMELINE_SEVERITY_THRESHOLD = 0.5

const ANALYZER_LABELS: Record<string, string> = {
  clipping: "Clipping",
  dropout: "Dropout",
  loop_detection: "Loop",
  phoneme_stretch: "Phoneme Stretch",
  syllable_rate: "Syllable Rate",
  voice_quality: "Voice Quality",
  timbre_drift: "Timbre Drift",
  pause_detection: "Pause",
}

const QUALITY_ANALYZERS = ["voice_quality"]

const SEVERITY_ANALYZERS = ["clipping", "dropout", "timbre_drift"]

const DIAGNOSTIC_ANALYZERS = [
  "syllable_rate",
  "loop_detection",
  "phoneme_stretch",
  "pause_detection",
]

const ANALYZER_ORDER = [
  ...QUALITY_ANALYZERS,
  ...SEVERITY_ANALYZERS,
  ...DIAGNOSTIC_ANALYZERS,
]

const ARTIFACT_TYPES = new Set([
  "clipping",
  "dropout",
  "codec",
  "Codec Artifact",
  "Clipping",
  "Dropout",
])
const VOICE_QUALITY_TYPES = new Set([
  "voice_quality",
  "voice_quality_degradation",
  "Voice Quality Degradation",
])

function getChipStyle(stringValue: string) {
  if (ARTIFACT_TYPES.has(stringValue)) {
    return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800"
  }
  if (VOICE_QUALITY_TYPES.has(stringValue)) {
    return "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800"
  }
  return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
}

function getSeverityTextColor(stringValue: string) {
  if (ARTIFACT_TYPES.has(stringValue)) {
    return "text-purple-600 dark:text-purple-400"
  }
  if (VOICE_QUALITY_TYPES.has(stringValue)) {
    return "text-teal-600 dark:text-teal-400"
  }
  return "text-blue-600 dark:text-blue-400"
}

const fmt = (sec: number) =>
  `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`

function getRegionColor(stringValue: string, severity: number): string {
  const alpha = Math.min(0.15 + severity * 0.3, 0.6).toFixed(2)
  if (ARTIFACT_TYPES.has(stringValue)) {
    return `rgba(147, 51, 234, ${alpha})`
  }
  if (VOICE_QUALITY_TYPES.has(stringValue)) {
    return `rgba(20, 184, 166, ${alpha})`
  }
  return `rgba(59, 130, 246, ${alpha})`
}

function getAnalyzerCardStyle(quality: number) {
  if (quality <= 0.15) {
    return {
      bg: "bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800",
      label: "text-sm text-red-600 font-medium mb-1 dark:text-red-400",
      value: "text-xl font-bold text-red-800 dark:text-red-200",
    }
  }
  if (quality <= 0.6) {
    return {
      bg: "bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800",
      label: "text-sm text-orange-600 font-medium mb-1 dark:text-orange-400",
      value: "text-xl font-bold text-orange-800 dark:text-orange-200",
    }
  }
  return {
    bg: "bg-surface-1 border border-coval-border",
    label: "text-sm text-coval-text-muted font-medium mb-1",
    value: "text-xl font-bold text-coval-text-muted",
  }
}

function getQualityTier(score: number): {
  label: string
  color: NonNullable<BadgeProps["variant"]>
} {
  if (score >= 0.8) {
    return { label: "Good", color: "success" }
  }
  if (score >= 0.6) {
    return { label: "Fair", color: "info" }
  }
  if (score >= 0.4) {
    return { label: "Poor", color: "warning" }
  }
  return { label: "Severe", color: "danger" }
}

function renderAnalyzerSection(
  title: string,
  analyzers: string[],
  perAnalyzerScores: Record<string, number>,
  analyzerEventCounts: Record<string, number>,
  showAsQuality: boolean,
) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
      <div
        className={`grid gap-3 ${analyzers.length >= 3 ? "grid-cols-3" : analyzers.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}
      >
        {analyzers.map((analyzerName) => {
          const severity = perAnalyzerScores[analyzerName]
          const isSkipped = severity === undefined
          const displayValue = isSkipped
            ? 0
            : showAsQuality
              ? 1 - severity
              : severity
          const eventCount = analyzerEventCounts[analyzerName] ?? 0
          const quality = isSkipped ? 1 : 1 - severity
          const style = getAnalyzerCardStyle(quality)

          return (
            <div key={analyzerName} className={`rounded-lg p-3 ${style.bg}`}>
              <div className={style.label}>
                {ANALYZER_LABELS[analyzerName] ?? analyzerName}
              </div>
              {isSkipped ? (
                <div className="text-sm text-coval-text-muted italic">
                  skipped
                </div>
              ) : (
                <>
                  <div className={style.value}>{displayValue.toFixed(2)}</div>
                  <div className="text-xs text-coval-text-muted mt-1">
                    {eventCount} {eventCount === 1 ? "event" : "events"}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export class SpeechArtifactAnomalyMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "score"
  aggregate_units = "score"

  renderMetricType(): string {
    return "Speech Artifact Anomaly"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const compositeScore = mo.float_value ?? 0
    const resultData = mo.result as Record<string, unknown> | undefined
    const rawValues = (resultData?.raw_values ?? {}) as Record<string, number>
    const totalAnomalyCount = (resultData?.count as number) ?? 0

    const stats = (resultData?.stats ?? []) as Array<Record<string, unknown>>
    const perAnalyzerScoresEntry = stats.find(
      (stat) =>
        stat.stats_type === "custom" && stat.name === "per_analyzer_scores",
    )
    const perAnalyzerScores = (perAnalyzerScoresEntry?.values ?? {}) as Record<
      string,
      number
    >

    const tier = getQualityTier(compositeScore)

    const analyzerEventCounts: Record<string, number> = {}
    const countsFromRawValues = new Set<string>()
    for (const [key, value] of Object.entries(rawValues)) {
      if (
        key.endsWith("_event_count") ||
        key.endsWith("_anomalous_pause_count")
      ) {
        const matchedAnalyzer = ANALYZER_ORDER.find((name) =>
          key.startsWith(`${name}_`),
        )
        if (matchedAnalyzer) {
          analyzerEventCounts[matchedAnalyzer] =
            (analyzerEventCounts[matchedAnalyzer] ?? 0) +
            (typeof value === "number" ? value : 0)
          countsFromRawValues.add(matchedAnalyzer)
        }
      }
    }
    const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
    const LABEL_TO_ANALYZER: Record<string, string> = {
      voice_quality_degradation: "voice_quality",
      "Voice Quality Degradation": "voice_quality",
      timbre_drift: "timbre_drift",
      "Timbre drift from anchor": "timbre_drift",
      "Timbre drift from rolling": "timbre_drift",
      pitch_shift: "timbre_drift",
      syllable_rate_anomaly: "syllable_rate",
      "Syllable Rate Anomaly": "syllable_rate",
      pause_anomaly: "pause_detection",
      "Anomalous Pause": "pause_detection",
    }
    for (const sv of subvalues) {
      const label = sv.string_value || ""
      const analyzer = LABEL_TO_ANALYZER[label]
      if (analyzer && !countsFromRawValues.has(analyzer)) {
        analyzerEventCounts[analyzer] = (analyzerEventCounts[analyzer] ?? 0) + 1
      }
    }

    const severeAnomalies = subvalues.filter(
      (sv) => (sv.float_value ?? 0) >= TIMELINE_SEVERITY_THRESHOLD,
    )
    const hiddenCount = subvalues.length - severeAnomalies.length

    const handleSeek = (e: React.MouseEvent, timestamp: number) => {
      e.stopPropagation()
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
        time: timestamp,
      })
    }

    const openLabels = new Set<string>()

    const syncRegions = (grouped: Map<string, SubvalueByTimestamp[]>) => {
      if (openLabels.size === 0) {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
          regions: null,
        })
        return
      }
      const filtered: SubvalueByTimestamp[] = []
      for (const label of openLabels) {
        const events = grouped.get(label)
        if (events) {
          filtered.push(...events)
        }
      }
      const regions: AudioRegionParams[] = filtered.map((sv, idx) => {
        const severity = sv.float_value ?? 0
        const color = getRegionColor(sv.string_value ?? "", severity)
        return {
          id: `speech_artifact_anomaly_hover:${idx}`,
          start: sv.start_offset,
          end: Math.max(sv.end_offset, sv.start_offset + 0.5),
          color,
          drag: false,
          resize: false,
        }
      })
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
        regions,
      })
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold">
            {compositeScore.toFixed(3)}
          </span>
          <Badge variant={tier.color}>{tier.label}</Badge>
          <span className="text-sm text-coval-text-muted">
            {totalAnomalyCount} anomalies detected
          </span>
        </div>

        {renderAnalyzerSection(
          "Quality",
          QUALITY_ANALYZERS,
          perAnalyzerScores,
          analyzerEventCounts,
          true,
        )}
        {renderAnalyzerSection(
          "Severity",
          SEVERITY_ANALYZERS,
          perAnalyzerScores,
          analyzerEventCounts,
          false,
        )}
        {renderAnalyzerSection(
          "Diagnostic Measurements",
          DIAGNOSTIC_ANALYZERS,
          perAnalyzerScores,
          analyzerEventCounts,
          false,
        )}

        {severeAnomalies.length > 0 &&
          (() => {
            const TIMBRE_DRIFT_PREFIXES = [
              "Timbre drift",
              "Timbre + pitch",
              "Pitch shift",
              "timbre_drift",
              "pitch_shift",
            ]

            function getGroupLabel(rawLabel: string): string {
              if (TIMBRE_DRIFT_PREFIXES.some((p) => rawLabel.startsWith(p))) {
                return "Timbre Drift"
              }
              return rawLabel
            }

            const grouped = new Map<string, SubvalueByTimestamp[]>()
            for (const sv of severeAnomalies) {
              const rawLabel = sv.string_value || "anomaly"
              const label = getGroupLabel(rawLabel)
              const existing = grouped.get(label) ?? []
              existing.push(sv)
              grouped.set(label, existing)
            }

            return (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  Severe Anomalies ({severeAnomalies.length} of{" "}
                  {subvalues.length})
                </h3>
                <div className="space-y-2">
                  {Array.from(grouped.entries()).map(([label, events]) => (
                    <details
                      key={label}
                      className="group"
                      onClick={(e) => e.stopPropagation()}
                      onToggle={(e) => {
                        const isOpen = (e.target as HTMLDetailsElement).open
                        if (isOpen) {
                          openLabels.add(label)
                        } else {
                          openLabels.delete(label)
                        }
                        syncRegions(grouped)
                      }}
                    >
                      <summary
                        className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer border-coval-border bg-surface-1 hover:bg-surface-1-hover list-none"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-xs select-none transition-transform group-open:rotate-90">
                          ▶
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold border ${getChipStyle(label)}`}
                        >
                          {label}
                        </span>
                        <span className="text-sm text-coval-text-muted">
                          {events.length}{" "}
                          {events.length === 1 ? "event" : "events"}
                        </span>
                      </summary>
                      <div className="space-y-1 mt-1 ml-6">
                        {events.map((sv, i) => {
                          const severity = sv.float_value ?? 0
                          const borderColor =
                            severity >= 0.8
                              ? "border-l-red-500"
                              : severity >= 0.5
                                ? "border-l-orange-400"
                                : "border-l-default-300"
                          return (
                            <div
                              key={i}
                              className={`flex items-center gap-2 px-3 py-1.5 border border-l-3 rounded-lg transition-colors cursor-pointer border-coval-border bg-surface-1 hover:bg-surface-1-hover ${borderColor}`}
                              onClick={(e) => handleSeek(e, sv.start_offset)}
                            >
                              <span className="text-sm text-coval-text-muted font-mono">
                                {fmt(sv.start_offset)} – {fmt(sv.end_offset)}
                              </span>
                              <span className="text-xs text-coval-text-muted">
                                ({(sv.end_offset - sv.start_offset).toFixed(1)}
                                s)
                              </span>
                              <span
                                className={`ml-auto text-xs font-mono ${getSeverityTextColor(label)}`}
                              >
                                severity: {(sv.float_value ?? 0).toFixed(2)}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </details>
                  ))}
                </div>
                {hiddenCount > 0 && (
                  <div className="text-xs text-coval-text-muted mt-2 pl-4">
                    + {hiddenCount} lower-severity events
                  </div>
                )}
              </div>
            )
          })()}

        {severeAnomalies.length === 0 && subvalues.length > 0 && (
          <div className="text-sm text-coval-text-muted">
            {subvalues.length} anomalies detected, none above severity threshold
            ({TIMELINE_SEVERITY_THRESHOLD})
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const color = getRegionColor(item.string_value ?? "", severity)
          return {
            id: `speech_artifact_anomaly:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.5),
            color,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: "speech_artifact_anomaly",
      scoreType: "raw_count",
      buildReviewedResultJson: ({ score }) => ({
        anomaly_count: Number(score.toFixed(0)),
      }),
    }
  }
}
